import { Component, FC, ReactNode } from 'react'
import * as styles from 'components/features/insights/InsightButton/InsightButton.css'
import Button, { ButtonSize } from 'components/features/pfm/Button/Button'
import { NavigationLink } from 'components/atoms/navigation/NavigationLink'
import { Anchor } from 'interfaces/navigation'
import { InsightButtonTheme } from 'interfaces/insights'
import { Omit } from 'interfaces'
import { ShadowKind } from 'interfaces/component'
import cn from 'classnames'

export const NoLink: FC = ({ children }) => (
  <>{ children }</>
)

export const Link: FC<Omit<InsightButtonLinkProps, 'linkComponent'>> = ({ children, to, target }) => (
  <a target={ target } href={ to }>{ children }</a>
)

type InsightButtonNavigationLinkProps = { linkComponent: typeof NavigationLink, to: Anchor }
type InsightButtonLinkProps = { linkComponent: typeof Link, to: string, target?: '_blank' }
type InsightButtonNoLinkProps = { linkComponent: typeof NoLink }
type InsightButtonLink =
  | InsightButtonNavigationLinkProps
  | InsightButtonLinkProps
  | InsightButtonNoLinkProps


function isNavigationLinkProps (props: InsightButtonLink): props is InsightButtonNavigationLinkProps {
  return props.linkComponent === NavigationLink
}
function isLinkProps (props: InsightButtonLink): props is InsightButtonLinkProps {
  return props.linkComponent === Link
}
function isNoLinkProps (props: InsightButtonLink): props is InsightButtonNoLinkProps {
  return props.linkComponent === NoLink
}

export type InsightButtonProps =
  & InsightButtonLink
  & {
    // this is needed because `children` are being passed via props object notation
    children: ReactNode
    theme?: InsightButtonTheme | null
    size?: ButtonSize
    fullWidth?: boolean
    shadow?: ShadowKind
    onClick: () => void
  }

class InsightButton extends Component<InsightButtonProps> {
  render () {
    const {
      children,
      theme = 'blue',
      size = 'mini',
      fullWidth,
      shadow,
      onClick,
    } = this.props

    const SpecificLink = this.getSpecificLinkComponent()

    const buttonTheme = theme === 'text-only' || theme === 'outlined' ? 'blue' : theme

    const textOnlyStyle = theme === 'text-only' ? 'textOnly' : undefined
    const outlinedStyle = theme === 'outlined' ? 'outlined' : undefined

    return (
      <span className={ cn(styles.insightButton, fullWidth && styles.fullWidth) } data-allow-propagation>
        <SpecificLink>
          <Button
            shadow={ shadow }
            theme={ buttonTheme || undefined }
            style={ textOnlyStyle || outlinedStyle }
            size={ size }
            fullWidth={ fullWidth }
            onClick={ onClick }
          >
            { children }
          </Button>
        </SpecificLink>
      </span>
    )
  }

  private getSpecificLinkComponent = (): FC => {
    const { props } = this

    if (isNavigationLinkProps(props)) {
      return ({ children }) => (
        <NavigationLink to={ props.to }>{ children }</NavigationLink>
      )
    }

    if (isLinkProps(props)) {
      return ({ children }) => (
        <Link to={ props.to } target={ props.target }>{ children }</Link>
      )
    }

    if (isNoLinkProps(props)) {
      return ({ children }) => (
        <NoLink>{ children }</NoLink>
      )
    }

    return () => null
  }
}

export default InsightButton
